import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import {
  MagnifyingGlass, CaretDown, BookOpen, Microphone, SignIn, CurrencyDollar,
  ShieldCheck, UserCircle, Envelope, GraduationCap,
} from "@phosphor-icons/react";

const FAQ = [
  {
    cat: "Getting started", icon: <BookOpen size={16}/>,
    items: [
      { q: "What is Ekkol?", a: "Ekkol is a voice-first memorization app. It reads your study material aloud, listens while you repeat it back, and scores your accuracy. Study on walks, during commutes, or at your desk — anywhere you can speak aloud safely." },
      { q: "Who is it for?", a: "Students (exam prep, vocabulary, class notes), professionals (ITIL, PMP, AWS certifications), public speakers (rehearsing speeches), and anyone who learns better by speaking aloud." },
      { q: "Do I need an account?", a: "Yes — Ekkol uses Google sign-in (fastest, no passwords to remember). You can try the landing page demo without an account." },
      { q: "How do I create my first deck?", a: "After signing in, click ‘New deck’. You can paste a script, upload a PDF/Word document, or paste multiple-choice questions and Ekkol will convert them into flashcards automatically." },
      { q: "What's the difference between Script and Q&A modes?", a: "Script mode breaks text into chunks and asks you to repeat each chunk verbatim — great for speeches or passages. Q&A mode builds flashcards where Ekkol reads the question and scores your spoken answer." },
    ],
  },
  {
    cat: "For students", icon: <BookOpen size={16}/>,
    items: [
      {
        q: "My teacher just invited me — what do I do?",
        a: (
          <span>
            Three steps: <strong>(1)</strong> Sign in with your Google account. <strong>(2)</strong> If you got a 6-letter code, click <Link to="/classes" className="link-underline font-medium">Classes</Link> → <em>Join with code</em>. If your teacher invited you by email, you&apos;re already enrolled — just check your Library. <strong>(3)</strong> Open the assigned deck and hit <em>Practice</em>.
          </span>
        ),
      },
      {
        q: "Wait — do I need to already know the material before practicing?",
        a: "Yes! Ekkol helps you REMEMBER stuff — it doesn't teach it for the first time. Before practicing, make sure you've already: read the chapter, watched the lecture, reviewed the slides, or studied your notes. Ekkol then helps you lock the information into your long-term memory so you don't forget it before the exam.",
      },
      {
        q: "How long and how often should I practice?",
        a: "Short and often beats long and rare. 5-10 minutes, 3-5 times a week, is way better than one 60-minute cram the night before a test. Your brain needs sleep between sessions to actually store the material.",
      },
      {
        q: "I keep missing the same card — what do I do?",
        a: "Three things: (1) Tap the 💡 hint button — it gives you small clues, not the answer. (2) Go back to your class notes or textbook and re-read the part that card is about. (3) If a card is really long, open the deck and tap the Scissors icon on the card to split it into two shorter chunks.",
      },
      {
        q: "What if I don't have my phone — can I practice on my computer?",
        a: "Yes! Ekkol works on any device with a microphone and internet. Phone, tablet, laptop, Chromebook — all fine. Chrome or Edge browsers work best.",
      },
      {
        q: "Is it okay to practice while I'm doing other stuff?",
        a: "Totally — that's what Ekkol is built for. Hands-free mode is designed for walking the dog, cleaning your room, riding the bus, or waiting for practice to start. Just toggle Hands-free ON and speak your answers out loud.",
      },
      {
        q: "My teacher assigned a deck but I don't see it",
        a: "Make sure you signed in with the SAME Google account your teacher invited (not a different one). Still missing? Ask your teacher to invite you again or give you the 6-letter class code so you can join directly.",
      },
      {
        q: "Can I keep using Ekkol after this class ends?",
        a: "Yes! You keep your Ekkol account forever. All the decks you've studied stay in your Library. You can make your own decks for other classes, college apps, driver's permit test, hobbies — anything you want to memorize. The Free plan stays free.",
      },
      {
        q: "I'm embarrassed to talk out loud — is that okay?",
        a: "Totally normal. A few ideas: use headphones with a mic so you can speak quietly, practice somewhere private first (bedroom, car, walk outside), or use the 'type instead' fallback if voice feels weird. You'll get comfortable fast — most students prefer speaking after a week.",
      },
      {
        q: "What's with the beep?",
        a: "The beep means \"your turn\" — after you hear it, start speaking. If it's annoying, go to Profile → Beep volume and turn it down (or all the way off). You can also switch to Auto mode which skips the beep entirely.",
      },
    ],
  },
  {
    cat: "Voice & hands-free", icon: <Microphone size={16}/>,
    items: [
      { q: "How does hands-free mode work?", a: "Toggle ‘Hands-free ON’ at the top of any study session. Ekkol will read the card, wait for you to speak, score your answer, and auto-advance — all without touching the screen. Say ‘next’, ‘previous’, ‘repeat’, ‘hint’, ‘pause’, or ‘stop’ anytime." },
      { q: "My mic isn't working", a: "1) Make sure your browser has mic permission (click the 🔒 in the address bar → Allow microphone). 2) Use Chrome or Edge — Safari and Firefox speech recognition is less reliable. 3) Plug in headphones BEFORE loading the page. 4) Refresh if you switched input devices." },
      { q: "Why did my answer fail even though it sounded right?", a: "Three common causes: (a) You're in Exact match mode — switch to Concept match (more lenient). (b) Your answer was a synonym not included in the card — add ‘Alternate accepted answers’ to the card (e.g. ‘Windows 10 | Win10’). (c) Background noise confused the recognizer — try a quieter space or use a headset." },
      { q: "Can I speak WHILE Ekkol is reading?", a: "Yes — go to Profile → When to listen → ‘Barge-in’. The mic stays open while the app is reading, so you can answer immediately. Great for multi-taskers but may pick up background noise." },
      { q: "The beep is too soft / too loud", a: "Profile → Beep volume. Slide to 0% to mute entirely, or up to 150% for noisier environments. There's a ‘Test beep’ button right next to the slider." },
      { q: "What voice commands work?", a: "‘next’, ‘skip’, ‘previous’, ‘go back’, ‘repeat’, ‘again’, ‘hint’, ‘help’, ‘clue’, ‘pause’, ‘wait’, ‘stop’, ‘end’, ‘quit’." },
    ],
  },
  {
    cat: "Hints & scoring", icon: <GraduationCap size={16}/>,
    items: [
      { q: "How do AI hints work?", a: "Tap the 💡 Hint button (or say ‘hint’) and Ekkol gives you a level-1 clue. Tap again for a bigger hint (level 2), tap a third time for a near-reveal (level 3). Each hint escalates without spoiling the answer." },
      { q: "Can Ekkol auto-give hints when I'm stuck?", a: "Yes — Profile → Auto-hint after misses. Set it to ‘After 1’, ‘After 2’, or ‘After 3’. Level escalates automatically with each miss." },
      { q: "What happens if I miss a card 3 times?", a: "Profile → If I miss a card → ‘After 3 misses’ reads the correct answer aloud. Alternatively ‘At end of run’ shows all failed cards at the end so you can retry them." },
      { q: "What is concept matching?", a: "Instead of checking exact word overlap, concept match uses AI to decide if your answer means the same thing as the target. ‘The sponsor authorizes the budget’ matches ‘Authorizes budget’ at ~95%." },
      { q: "My card has two correct answers — how do I handle that?", a: "When creating or editing a flashcard, use the ‘Alternate accepted answers’ field. Separate with | (pipe): ‘Windows 10 | Windows 11 | Win10’. Any of them will pass." },
    ],
  },
  {
    cat: "Account & billing", icon: <UserCircle size={16}/>,
    items: [
      {
        q: "How do I cancel my subscription?",
        a: (
          <span>
            Go to <Link to="/profile" className="link-underline font-medium">Profile → Plan &amp; billing</Link>, then click the <strong>Switch to Free plan</strong> button. You keep Plus features until the end of your billing period. You can re-subscribe anytime.
          </span>
        ),
      },
      {
        q: "How do I delete my account?",
        a: (
          <span>
            Go to <Link to="/profile" className="link-underline font-medium">Profile → Danger zone</Link> → <strong>Delete my account</strong>. This is permanent — all decks, progress, and data will be erased within 30 days. We cannot recover deleted accounts.
          </span>
        ),
      },
      { q: "Can I export my data?", a: "Yes — Profile → Download my data. You'll get a JSON file of your decks, cards, and study history. Takes about 30 seconds." },
      { q: "Do I need a credit card to sign up?", a: "No. Free tier forever, no credit card required. You only enter payment info if you upgrade to Plus or Edu." },
      { q: "Student discount?", a: "50% off Plus forever with a .edu email. Email hello@ekkol.app from your university address and we'll send you a promo code." },
      { q: "Do you have a free trial of Plus?", a: "Yes — 7 days free, no credit card. When it ends, your account automatically drops back to Free with no surprise charges." },
    ],
  },
  {
    cat: "Privacy & security", icon: <ShieldCheck size={16}/>,
    items: [
      { q: "Is my speech data stored?", a: "No. Speech recognition runs in your browser — audio never leaves your device. Only the resulting text (your spoken answer) is sent to Ekkol for scoring." },
      { q: "What does Ekkol store about me?", a: "Name, email, profile picture (from Google), your decks and cards, and your study attempts (for progress tracking). Full details in our Privacy Policy." },
      { q: "Can other people see my decks?", a: "Only if you explicitly mark a deck as public. Private decks are visible to you alone. You can change this anytime in the deck settings." },
      { q: "Where is my data stored?", a: "On secure US-based servers (AWS/Emergent infrastructure). We do not sell data, run ads, or train AI on your content." },
    ],
  },
  {
    cat: "Teachers & classes", icon: <GraduationCap size={16}/>,
    items: [
      {
        q: "I'm a teacher — how do I get started?",
        a: (
          <span>
            Three steps: <strong>(1)</strong> Make a deck first — click <Link to="/decks/new" className="link-underline font-medium">New deck</Link> and paste your study material, upload a PDF, or let AI generate flashcards from a topic. <strong>(2)</strong> Go to <Link to="/classes" className="link-underline font-medium">Classes</Link> and click <em>Create class</em> — you'll get a 6-letter join code. Share the code with students in class, by email, or paste the invite link. <strong>(3)</strong> Inside the class, click <em>New assignment</em> and pick one of your decks — students see it in their library instantly.
          </span>
        ),
      },
      {
        q: "Do I need to make a deck before I can assign anything?",
        a: "Yes. Assignments are always backed by one of your own decks, so create the deck first, then assign it. You can edit the deck later and the change flows to every student who has it.",
      },
      {
        q: "How do students join my class?",
        a: "Two ways. (1) They sign in to Ekkol and click Classes → Join with code, then enter your 6-letter code. (2) You invite them by email on the Roster tab — we auto-enroll them on their next sign-in (no extra steps on their end). Paste a CSV roster if you have a class list.",
      },
      {
        q: "Do my students need to pay?",
        a: "No. Students can join on the Free plan and practice your assigned decks without an Edu subscription. Only the teacher needs Edu to create classes and see progress.",
      },
      {
        q: "How do I track student progress?",
        a: "Open your class → Progress tab. You'll see a table with every student as a row, every assignment as a column. Each cell shows cards-mastered / total cards, an accuracy %, and a green check when they've mastered ≥80% of the deck. Click Export CSV to download for your gradebook.",
      },
      {
        q: "Can I export grades to my gradebook?",
        a: "Yes — on the Progress tab, click Export CSV. You'll get a file with student name, email, and each assignment's mastery % and accuracy %, ready to import into Google Sheets, Excel, PowerSchool, Infinite Campus, or any gradebook.",
      },
      {
        q: "Can I share my deck directly to Google Classroom?",
        a: "Yes. Open any public deck and click the Share to Google Classroom button — it uses Google's official widget to post the deck as an assignment or material in your existing class. Make sure the deck is marked Public first (toggle at the top of the deck view).",
      },
      {
        q: "Is there SCORM / LTI / Canvas / Blackboard / Moodle support?",
        a: (
          <span>
            Not yet, but it's on the roadmap. Today: <strong>Google Classroom share is live</strong> (one-click). <strong>SCORM 1.2 export</strong>, <strong>LTI 1.3</strong> for Canvas/Blackboard/Moodle, and <strong>PowerSchool gradebook sync</strong> are planned. If you're at a district that needs one of these, email <a href="mailto:sales@ekkol.app" className="link-underline font-medium">sales@ekkol.app</a> — early-access slots are free while we build.
          </span>
        ),
      },
      {
        q: "Can I remove a student?",
        a: "Yes — Roster tab → click the remove icon next to their name. They lose access to the class and your assignments, but keep any personal decks they've made.",
      },
      {
        q: "What if a student is stuck / failing?",
        a: "Check the Progress tab — any cell with a red circle means they've attempted but not mastered. Suggest they (a) open the deck in Study session and practice, (b) use Smart hints (💡 button), and (c) try Concept match mode if the deck is strict. You can also Split long cards (Scissors icon) into smaller chunks to make them easier.",
      },
    ],
  },
  {
    cat: "Schools, pricing & compliance", icon: <UserCircle size={16}/>,
    items: [
      { q: "How much does Edu cost?", a: "$4.99 per student per month (or $49/student/year). Minimum 5 students, max 50 per class. Above 50 students per cohort, contact sales for Enterprise pricing." },
      { q: "What about enterprise/corporate training?", a: "We custom-quote enterprise based on seat count, SSO, SCORM/LMS requirements, and SLA. Email sales@ekkol.app or click ‘Contact Sales’ on the pricing page." },
      { q: "Is Ekkol FERPA/COPPA compliant?", a: "Ekkol is designed for learners 13+. We do not knowingly collect data from children under 13. For FERPA-covered institutions, reach out to sales@ekkol.app — we can sign a Data Processing Agreement." },
      { q: "Can I get a free trial or educator promo code?", a: "Yes — email sales@ekkol.app with your school email and how you plan to use it. We hand out promo codes to educators exploring Ekkol, and offer 14-day Edu trials before you commit." },
    ],
  },
];

export default function Help() {
  const [search, setSearch] = useState("");
  const [openItem, setOpenItem] = useState(null);

  // Scroll to the #hash anchor on mount (e.g. /help#teachers-classes)
  useEffect(() => {
    const hash = window.location.hash?.replace(/^#/, "");
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
    }
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return FAQ;
    return FAQ.map((cat) => ({
      ...cat,
      items: cat.items.filter((it) => {
        const hay = (it.q + " " + (typeof it.a === "string" ? it.a : "")).toLowerCase();
        return hay.includes(q);
      }),
    })).filter((cat) => cat.items.length > 0);
  }, [search]);

  return (
    <div className="min-h-screen bg-[color:var(--paper)]">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 md:px-10 py-16 fade-in">
        <div className="mb-10 text-center">
          <div className="eyebrow mb-3">Help Center</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            How can we help?
          </h1>
          <p className="text-[color:var(--ink-muted)] mt-4 max-w-xl mx-auto">
            Answers to the most common questions. Can&apos;t find it? <a href="mailto:support@ekkol.app" className="link-underline">support@ekkol.app</a>
          </p>
        </div>

        <div className="relative mb-10">
          <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--ink-muted)]"/>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search the help center…"
            className="w-full bg-white border rule pl-12 pr-4 py-4 font-serif text-lg focus:outline-none focus:border-[color:var(--ink)]"
            data-testid="help-search"
          />
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16 text-[color:var(--ink-muted)]" data-testid="help-empty">
            <p>No articles match &ldquo;{search}&rdquo;.</p>
            <a href="mailto:support@ekkol.app" className="btn-primary inline-flex mt-6">
              <Envelope size={14}/> Email support
            </a>
          </div>
        )}

        {filtered.map((cat, ci) => {
          const slug = cat.cat.toLowerCase().replace(/[^\w]+/g, "-");
          return (
          <section key={cat.cat} id={slug} className="mb-10 scroll-mt-20" data-testid={`help-cat-${ci}`}>
            <div className="flex items-center gap-2 eyebrow mb-4 text-[color:var(--oxford)]">
              {cat.icon} {cat.cat}
            </div>
            <div className="bg-white border rule divide-y rule">
              {cat.items.map((it, i) => {
                const id = `${ci}-${i}`;
                const isOpen = openItem === id;
                return (
                  <div key={i} data-testid={`help-item-${id}`}>
                    <button
                      onClick={() => setOpenItem(isOpen ? null : id)}
                      className="w-full text-left px-6 py-4 flex items-center justify-between gap-4 hover:bg-[color:var(--surface-alt)] transition-colors"
                    >
                      <span className="font-serif text-lg">{it.q}</span>
                      <CaretDown size={16} className={`shrink-0 text-[color:var(--ink-muted)] transition-transform ${isOpen ? "rotate-180" : ""}`}/>
                    </button>
                    {isOpen && (
                      <div className="px-6 pb-5 pt-1 text-[color:var(--ink-muted)] fade-in leading-relaxed">
                        {typeof it.a === "string" ? <p>{it.a}</p> : it.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
          );
        })}

        <div className="mt-16 bg-[color:var(--ink)] text-white p-10 text-center">
          <div className="eyebrow text-[color:var(--vermilion)] mb-3">Still stuck?</div>
          <h2 className="font-serif text-3xl mb-4">We read every email.</h2>
          <p className="text-white/70 mb-6">Usually replies within 24 hours, often sooner.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href="mailto:support@ekkol.app" className="inline-flex items-center gap-2 bg-transparent hover:bg-white/10 text-white border border-white/40 hover:border-white px-5 py-3 text-sm font-mono uppercase tracking-widest transition-colors" data-testid="help-cta-support">
              <Envelope size={14}/> support@ekkol.app
            </a>
            <a href="mailto:sales@ekkol.app" className="inline-flex items-center gap-2 bg-transparent hover:bg-white/10 text-white border border-white/40 hover:border-white px-5 py-3 text-sm font-mono uppercase tracking-widest transition-colors" data-testid="help-cta-sales">
              <CurrencyDollar size={14}/> sales@ekkol.app
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}
